/**
 * AnimatedNumber — spring-physics count-up on mount or value change.
 *
 * Usage:
 *   <AnimatedNumber value={1234} />
 *   <AnimatedNumber value={points} format={(n) => `${n.toLocaleString('ar-SA')} نقطة`} />
 *   <AnimatedNumber value={price} format={formatSAR} duration={0.8} />
 *
 * Uses framer-motion useSpring so the number accelerates/decelerates naturally.
 * Respects prefers-reduced-motion: falls back to instant value.
 */
'use client';

import { useEffect, useRef } from 'react';
import { useSpring, useMotionValue } from 'framer-motion';

interface AnimatedNumberProps {
  /** The target number to display */
  value: number;
  /** Format the final number (default: toLocaleString) */
  format?: (n: number) => string;
  /** Spring stiffness (default 80 — gentle bounce) */
  stiffness?: number;
  /** Spring damping (default 20) */
  damping?: number;
  /** Override motion duration in seconds */
  duration?: number;
  className?: string;
}

export function AnimatedNumber({
  value,
  format,
  stiffness = 80,
  damping = 20,
  className,
}: AnimatedNumberProps) {
  const prefersReduced =
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false;

  const motionValue = useMotionValue(prefersReduced ? value : 0);
  const spring = useSpring(motionValue, { stiffness, damping, restDelta: 0.5 });

  const displayRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    motionValue.set(value);
  }, [value, motionValue]);

  useEffect(() => {
    if (prefersReduced) {
      if (displayRef.current) {
        displayRef.current.textContent = format
          ? format(Math.round(value))
          : Math.round(value).toLocaleString();
      }
      return;
    }
    const unsub = spring.on('change', (latest: number) => {
      if (displayRef.current) {
        const rounded = Math.round(latest);
        displayRef.current.textContent = format ? format(rounded) : rounded.toLocaleString();
      }
    });
    return unsub;
  }, [spring, format, value, prefersReduced]);

  const initial = format ? format(0) : '0';

  return (
    <span ref={displayRef} className={className}>
      {initial}
    </span>
  );
}

/**
 * AnimatedStat — labelled stat card with count-up value.
 * Drop-in replacement for plain <Stat label="..." value={n} /> patterns.
 */
interface AnimatedStatProps {
  label: string;
  value: number;
  format?: (n: number) => string;
  suffix?: string;
  className?: string;
}

export function AnimatedStat({ label, value, format, suffix, className }: AnimatedStatProps) {
  return (
    <div className={className}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold tabular-nums">
        <AnimatedNumber value={value} format={format} />
        {suffix && <span className="text-sm font-normal text-muted-foreground ml-1">{suffix}</span>}
      </p>
    </div>
  );
}
